/**
 * [CCI-15904] New AIFC Editor Experience — eligibility check.
 *
 * Returns true when the user is enrolled in the "New AIFC Editor Experience" — an umbrella
 * experiment that covers the Post Stream Review toolbar and the Full Page Staging Area.
 *
 * AND-gated with `platform_editor_ai_xstate_migration` because the new experience relies on
 * the xstate / orchestrator architecture (e.g. `buildCommitTransaction` only fires there).
 */
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

/**
 * Returns true when the current user is enrolled in the New AIFC Editor Experience
 * AND in the xstate migration. See file-level JSDoc above for details on what this
 * gate enables.
 *
 * This variant **logs an experiment exposure**. Use it only at canonical,
 * action-driven gate-enforcement points (e.g. building a commit transaction,
 * session-context generation, editor actions) — not in render paths that run on
 * every re-render, otherwise exposure counts will be inflated.
 */
export function isNewAifcExperienceEnabled(): boolean {
	return (
		expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true) &&
		expValEquals('platform_editor_ai_new_aifc_editor_experience', 'isEnabled', true)
	);
}

/**
 * Non-exposure-logging counterpart of `isNewAifcExperienceEnabled`.
 *
 * Use this at render-time / read-only call sites (e.g. deriving a prompt
 * placeholder) where the canonical exposure has already been logged at the
 * gate-enforcement point. This avoids inflating experiment exposure counts on
 * every re-render.
 */
export function isNewAifcExperienceEnabledNoExposure(): boolean {
	return (
		expValEqualsNoExposure('platform_editor_ai_xstate_migration', 'isEnabled', true) &&
		expValEqualsNoExposure('platform_editor_ai_new_aifc_editor_experience', 'isEnabled', true)
	);
}
